"use client";

import React, { useState } from 'react';
import HeroSection from './HeroSection-2';
import CTASection from './CTASection';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Star, Zap, Shield, Users, TrendingUp, Clock, Award, ChevronRight } from 'lucide-react';
import Navbar from '@/components/Navbar';

export default function ServicePage({ content }) {
  const {
    page,
    heading,
    description,
    heroImage,
    icon,
    overviewTitle,
    overviewText,
    approachTitle,
    approachText,
    growthTitle,
    growthText,
    serviceLists = [],
    taxOfficeLists = [],
    faqs = [],
    worksText,
    ctaTitle,
    ctaText,
    teamLink,
    testLink,
    overviewImage,
    servicesLabel,
        servicesImage,
    reclaimHours,
  } = content;

  const MotionSection = ({ children, variant = 'fade' }) => {
    const variants = {
      fade: { hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } },
      slideLeft: { hidden: { opacity: 0, x: -50 }, visible: { opacity: 1, x: 0, transition: { duration: 0.6 } } },
      slideRight: { hidden: { opacity: 0, x: 50 }, visible: { opacity: 1, x: 0, transition: { duration: 0.6 } } },
      zoomIn: { hidden: { opacity: 0, scale: 0.95 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.6 } } },
      rotateFade: { hidden: { opacity: 0, rotateX: -30 }, visible: { opacity: 1, rotateX: 0, transition: { duration: 0.6 } } },
    };
    return (
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.2 }}
        variants={variants[variant] || variants.fade}
      >
        {children}
      </motion.section>
    );
  };

  return (
    <div className="min-h-screen bg-[#fafafa] overflow-x-hidden">
      <Navbar />

      {/* Original Hero Section */}
      <HeroSection page={page} heading={heading} description={description} backgroundImage={heroImage} />

      

      {/* Growth Section */}
      <MotionSection variant="zoomIn">
        <section id="services" className="max-w-6xl mx-auto px-6 py-20 w-full overflow-x-hidden relative">
          
          {/* Background Gradient Mesh */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-50/60 via-transparent to-amber-50/40 pointer-events-none -z-10" />
          <div className="absolute top-20 left-10 w-64 h-64 bg-orange-400/10 rounded-full blur-3xl pointer-events-none -z-10" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-amber-400/8 rounded-full blur-3xl pointer-events-none -z-10" />
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-bold mb-4">
              Growth Strategy
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              How HibLink Can <span className="text-orange-500">Help You?</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={overviewImage || "/assets/services-default-og.jpg"}
                  alt={page || heading || 'Service'}
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-transparent" />
              </div>
              
            
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                    <Zap className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Fast & Efficient</h3>
                    <p className="text-slate-600">Quick response times and streamlined processes</p>
                  </div>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  {growthText}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                      <CheckCircle className="text-white" size={14} />
                    </div>
                    <span className="font-semibold text-slate-900">Scalable</span>
                  </div>
                  <p className="text-sm text-slate-600">Grow your team as your business expands</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                      <Shield className="text-white" size={14} />
                    </div>
                    <span className="font-semibold text-slate-900">Secure</span>
                  </div>
                  <p className="text-sm text-slate-600">Enterprise-grade security protocols</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <a
                  href={teamLink}
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-8 py-4 rounded-full transition-all shadow-lg hover:shadow-xl"
                >
                  Get Started Today
                  <ArrowRight size={20} />
                </a>
                <a
                  href="#list"
                  className="inline-flex items-center gap-3 bg-white border-2 border-slate-200 hover:border-orange-500 text-slate-700 hover:text-orange-600 font-bold px-8 py-4 rounded-full transition-all"
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </section>
      </MotionSection>

      {/* Services List */}
      {(serviceLists.length > 0 || taxOfficeLists.length > 0) && (
        <MotionSection variant="fade">
          <section id="list" className="max-w-7xl mx-auto px-6 py-20 relative">

            {/* Animated Dot Pattern Background */}
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(#f97316_1px,transparent_1px)] bg-[length:24px_24px] opacity-[0.08] -z-10" />
            <motion.div 
              animate={{ 
                x: [0, 30, 0],
                y: [0, -20, 0]
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-40 right-20 w-96 h-96 bg-orange-500/6 rounded-full blur-3xl pointer-events-none -z-10" 
            />
            <motion.div 
              animate={{ 
                x: [0, -20, 0],
                y: [0, 30, 0]
              }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute bottom-40 left-20 w-72 h-72 bg-amber-400/8 rounded-full blur-3xl pointer-events-none -z-10" 
            />
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-bold mb-4">
                What We Offer
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                {servicesLabel || 'Our Services'}
              </h2>
            </div>

                        {/* Reclaim Hours Banner (specific service pages only) */}
            {reclaimHours && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                viewport={{ once: false, amount: 0.5 }}
                className="mb-12"
              >
                <div className="max-w-4xl mx-auto bg-gradient-to-br from-orange-50 via-white to-orange-50 border border-orange-200/60 rounded-3xl px-8 py-8 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left shadow-sm">
                  <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600">
                    <Clock className="text-white" size={28} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl md:text-3xl font-bold text-slate-900 leading-snug">
                      Reclaim 20 Hours of Your Week
                    </h3>
                    <p className="text-slate-600 text-lg md:text-xl mt-1">
                      Focus on What Truly Matters.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* All Service Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {serviceLists.map((item, i) => {
                // Exact requested color scheme: ALL WHITE + 1 ORANGE + 1 DARK
                const style = i === 0 
                  ? { bg: 'bg-gradient-to-br from-orange-500 to-orange-600', text: 'text-white', desc: 'text-orange-50', accent: 'text-white' }
                  : i === 1 
                    ? { bg: 'bg-slate-900', text: 'text-white', desc: 'text-slate-300', accent: 'text-orange-400' }
                    : { bg: 'bg-white', text: 'text-slate-900', desc: 'text-slate-600', accent: 'text-orange-500' };
                const icons = [Zap, Shield, Award, Star, TrendingUp, Clock, Users, CheckCircle, ChevronRight];
                const RandomIcon = icons[i % icons.length];
                
                return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 120 + (i * 30), rotateX: 25, scale: 0.85 }}
                  whileInView={{ 
                    opacity: 1, 
                    y: 0, 
                    rotateX: 0, 
                    scale: 1,
                    transition: { 
                      type: "spring",
                      stiffness: 260,
                      damping: 20,
                      delay: i * 0.08
                    }
                  }}
                  viewport={{ once: false, amount: 0.3 }}
                  whileHover={{ 
                    y: -14, 
                    rotateX: 4,
                    rotateY: -6,
                    scale: 1.03,
                    transition: { duration: 0.35 } 
                  }}
                  style={{
                    transformStyle: 'preserve-3d',
                    perspective: '1200px'
                  }}
                  className={`rounded-2xl p-6 shadow-2xl transition-all duration-300 relative overflow-hidden ${style.bg} ${style.text}`}
                >
                  {/* Background gradient shine */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/8 via-transparent to-transparent pointer-events-none" />
                  
                  <h3 className="text-xl font-bold mb-3 relative z-10">{item}</h3>
                   <p className={`text-sm leading-relaxed mb-5 relative z-10 ${style.desc}`}>
                     Professional {item.toLowerCase()} solutions to streamline your business operations.
                   </p>
                  
                  <div className="flex justify-end relative z-10" style={{ transform: 'translateZ(30px)' }}>
                    <motion.div
                      animate={{ 
                        y: [0, -6, 0],
                        rotate: [0, 4, -3, 0],
                        scale: [1, 1.08, 1]
                      }}
                      transition={{ 
                        duration: 3 + (i * 0.4),
                        repeat: Infinity,
                        ease: "easeInOut",
                        repeatType: "reverse"
                      }}
                    >
                      <RandomIcon className={style.accent} size={30} strokeWidth={2} />
                    </motion.div>
                  </div>
                  
                  {/* 3D Depth Glow */}
                  <div className="absolute -bottom-3 -right-3 w-12 h-12 rounded-full bg-black/6 blur-2xl" />
                  <div className="absolute -top-2 -left-2 w-10 h-10 rounded-full bg-white/15 blur-xl" />
                </motion.div>
              )})}
            </div>

            {/* CTA Button */}
            <div className="text-center mt-12">
              <motion.a
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                href={teamLink}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-10 py-4 rounded-full transition-all shadow-lg hover:shadow-xl hover:scale-105"
              >
                Get Started Today
                <ArrowRight size={20} />
              </motion.a>
            </div>
        
          </section>
        </MotionSection>
      )}

      {/* How HibLink Works */}
      <MotionSection variant="fade">
        <section className="max-w-6xl mx-auto px-6 py-20 relative">
          
          {/* Gradient Wave Background */}
          <div className="absolute inset-0 bg-gradient-to-t from-orange-50/40 via-transparent to-transparent pointer-events-none -z-10" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-400/5 rounded-full blur-3xl pointer-events-none -z-10" />
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-bold mb-4">
              Our Process
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              How <span className="text-orange-500">HibLink Works</span>?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto mt-4">
              A simple, proven process to get your dedicated team up and running
            </p>
          </div>

            <div className="relative">
              {/* Vertical timeline line - animated draw on scroll */}
              <motion.div
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
                style={{ transformOrigin: 'top' }}
                viewport={{ once: false, amount: 0.1 }}
                className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-400 to-orange-600 transform -translate-x-1/2 z-0"
              ></motion.div>
              
              {/* Mobile vertical line - animated draw on scroll */}
              <motion.div
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                style={{ transformOrigin: 'top' }}
                viewport={{ once: false, amount: 0.1 }}
                className="lg:hidden absolute left-1/2 top-8 bottom-0 w-1 bg-gradient-to-b from-orange-400 to-orange-600 transform -translate-x-1/2 z-0"
              ></motion.div>
              
              <div className="space-y-16 lg:space-y-0">
                {/* Step 1 */}
                <div className="relative grid lg:grid-cols-2 gap-3 lg:gap-11 items-center">
                  <div className="lg:order-1 order-2 lg:pr-12 pt-8">
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6 }}
                      className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg border border-slate-100 mt-6 lg:mt-0"
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                          <Users className="text-white" size={24} />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-slate-900">Step 1: Team Matching</h3>
                          <p className="text-slate-600">We find the perfect team for your needs</p>
                        </div>
                      </div>
                      <p className="text-slate-700 leading-relaxed">
                        Tell us about your project, and we'll match you with a dedicated team of professionals who have the exact skills and experience you need.
                      </p>
                    </motion.div>
                  </div>
                  <div className="lg:order-2 order-1 relative lg:static">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6 }}
                      className="absolute left-1/2 top-0 lg:top-1/2 transform -translate-x-1/2 lg:-translate-y-1/2 w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 items-center justify-center shadow-lg z-10 flex"
                    >
                      <span className="text-white text-2xl font-bold">1</span>
                    </motion.div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="relative grid lg:grid-cols-2 gap-4 lg:gap-11 items-center">
                  <div className="lg:order-1 order-2"></div>
                  <div className="lg:order-2 order-1 lg:pl-12 pt-12">
                    <motion.div
                      initial={{ opacity: 0, x: -50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6 }}
                      className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg border border-slate-100 mt-6 lg:mt-0"
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                          <Clock className="text-white" size={24} />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-slate-900">Step 2: Onboarding</h3>
                          <p className="text-slate-600">Your team gets up to speed in 2 weeks</p>
                        </div>
                      </div>
                      <p className="text-slate-700 leading-relaxed">
                        We handle all the training and setup. Your team learns your processes, tools, and brand voice so they can hit the ground running.
                      </p>
                    </motion.div>
                  </div>
                  <div className="absolute left-1/2 top-0 lg:top-1/2 transform -translate-x-1/2 lg:-translate-y-1/2">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6 }}
                      className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 items-center justify-center shadow-lg z-10 flex"
                    >
                      <span className="text-white text-2xl font-bold">2</span>
                    </motion.div>
                  </div>
                </div>

               {/* Step 3 */}
               <div className="relative grid lg:grid-cols-2 gap-4 lg:gap-11 items-center">
                 <div className="lg:order-1 order-2 lg:pr-12 pt-8">
                   <motion.div
                     initial={{ opacity: 0, x: 50 }}
                     whileInView={{ opacity: 1, x: 0 }}
                     transition={{ duration: 0.6 }}
                     className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg border border-slate-100 mt-6 lg:mt-0"
                   >
                     <div className="flex items-center gap-4 mb-4">
                       <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                         <TrendingUp className="text-white" size={24} />
                       </div>
                       <div>
                         <h3 className="text-xl font-bold text-slate-900">Step 3: Launch & Scale</h3>
                         <p className="text-slate-600">Start delivering results and grow</p>
                       </div>
                     </div>
                     <p className="text-slate-700 leading-relaxed">
                       Your team goes live and starts delivering. We monitor performance and help you scale as your business grows.
                     </p>
                   </motion.div>
                 </div>
                 <div className="lg:order-2 order-1 relative lg:static">
                   <motion.div
                     initial={{ opacity: 0, scale: 0.8 }}
                     whileInView={{ opacity: 1, scale: 1 }}
                     transition={{ duration: 0.6 }}
                     className="absolute left-1/2 top-0 lg:top-1/2 transform -translate-x-1/2 lg:-translate-y-1/2 w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 items-center justify-center shadow-lg z-10 flex"
                   >
                     <span className="text-white text-2xl font-bold">3</span>
                   </motion.div>
                 </div>
               </div>
             </div>

       
          </div>
               {/* CTA Section */}
               <div className="text-center mt-19 z-10">
             
                               <a
                  href={teamLink}
                  className="inline-flex items-center gap-3 bg-white text-orange-600 font-bold px-8 py-4 rounded-full transition-all shadow-lg hover:shadow-xl hover:scale-105"
                >
                  Start Your Journey
                  <ArrowRight size={20} />
                </a>
            
            </div>
        </section>
      </MotionSection>

      

       {/* FAQ Section - Same design as /faq page */}
       <MotionSection variant="fade">
         <section className="py-20 px-6 bg-[#fafafa] relative overflow-hidden">
           {/* Background gradient overlay */}
           <div className="absolute inset-0 bg-gradient-to-r from-orange-50/60 via-transparent to-orange-50/40 pointer-events-none" />
           
           <div className="max-w-5xl mx-auto relative w-full">
             <div className="text-center mb-12">
               <div className="flex items-center justify-center gap-3 mb-3">
                 <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                 <span className="text-gray-500 font-medium">FAQs</span>
               </div>
               <h2 className="text-[clamp(2rem,4vw,3rem)] font-bold text-gray-800 leading-tight">
                 Frequently Asked Questions
               </h2>
             </div>

             <div className="space-y-3 w-full">
               {faqs && faqs.length > 0 && faqs.map((faq, index) => {
                 const [openIndex, setOpenIndex] = useState(0);
                 const toggle = (i) => {
                   setOpenIndex(openIndex === i ? null : i);
                 };
                 
                 return (
                   <div 
                     key={index} 
                     className={`bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm overflow-hidden transition-all duration-300 w-full ${
                       openIndex === index 
                         ? 'bg-gradient-to-br from-white to-orange-50/70 shadow-[0_10px_40px_rgba(255,123,0,0.12)] border-orange-200/50' 
                         : 'hover:bg-white/90 hover:shadow-md'
                     }`}
                   >
                     <button
                       onClick={() => toggle(index)}
                       className="w-full text-left px-7 py-6 flex justify-between items-center font-semibold text-lg text-gray-800"
                     >
                       <span className="break-words pr-4">{faq.question}</span>
                       <span className={`text-2xl transition-all duration-300 flex-shrink-0 ${openIndex === index ? 'rotate-45 text-orange-500' : 'text-gray-400'}`}>
                         {openIndex === index ? '✕' : '+'}
                       </span>
                     </button>
                     
                     {openIndex === index && (
                       <div className="px-7 pb-7 w-full">
                         <p className="text-gray-600 leading-relaxed text-lg break-words">
                           {faq.answer}
                         </p>
                       </div>
                     )}
                   </div>
                 );
               })}
             </div>
           </div>

           {/* CTA Button */}
           <div className="text-center mt-12">
             <motion.a
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               transition={{ delay: 0.3 }}
               href={teamLink}
               className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-10 py-4 rounded-full transition-all shadow-lg hover:shadow-xl hover:scale-105"
             >
               Get Started Today
               <ArrowRight size={20} />
             </motion.a>
           </div>

         </section>
       </MotionSection>

      <CTASection ctaTitle={ctaTitle} ctaText={ctaText} />
    </div>
  );
}
